/**
 * KARAKTER OLUŞTURMA KURALLARI
 *
 * Sihirbazın "seçenek göstermesi" ile "kuralı doğrulaması" ayrı işlerdir.
 * Bu dosya İKİNCİSİNİ yapar: saf kurallar, test edilebilir, UI'dan bağımsız.
 *
 * Sihirbaz (UI) bu fonksiyonları çağırır; kendi kural uydurmaz.
 * Böylece "en fazla 27 puan", "18'den büyük puan seçilemez" gibi kurallar
 * tek yerde yaşar ve testle korunur.
 */

import { abilityModifier } from './ability'
import { type Rng, rollFormula, secureRng } from './dice'
import {
  ABILITY_KEYS,
  type AbilityKey,
  type CharacterInput,
  type ClassEntry,
  type ContentIndex,
  type RaceEntry,
  SKILLS,
} from './types'

// ---------------------------------------------------------------------------
// YETENEK PUAN YÖNTEMLERİ
// ---------------------------------------------------------------------------

export const ABILITY_METHODS = ['standard', 'pointBuy', 'rolled'] as const
export type AbilityMethod = (typeof ABILITY_METHODS)[number]

/** 5e Standart Dizgi. Her puan bir kez kullanılır. */
export const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8] as const

/** Puan Havuzu bütçesi (5e). */
export const POINT_BUY_BUDGET = 27

/** Puan Havuzu alt sınırı / üst sınırı. */
export const POINT_BUY_MIN = 8
export const POINT_BUY_MAX = 15

/** Sihirbazda doğrulama yapılabilen en üst temel puan (20). */
export const SCORE_MAX = 20

/**
 * Puan Havuzu maliyeti.
 * 5e tablosu: 8=0, 9-13=1'er, 14=2, 15=3 (kademeli artış).
 */
export function pointBuyCost(score: number): number {
  if (score < POINT_BUY_MIN) return 0
  if (score <= 13) return score - POINT_BUY_MIN
  if (score === 14) return 7
  if (score === 15) return 9
  return Number.POSITIVE_INFINITY // 15 üstü puan havuzunda alınamaz
}

export interface PointBuyStatus {
  spent: number
  remaining: number
  valid: boolean
  /** Geçersizse neden? */
  error: string | null
}

/** Puan Havuzu durumunu hesaplar. */
export function evaluatePointBuy(scores: Record<AbilityKey, number>): PointBuyStatus {
  let spent = 0
  for (const key of ABILITY_KEYS) {
    const score = scores[key]
    if (score < POINT_BUY_MIN) {
      return {
        spent,
        remaining: POINT_BUY_BUDGET - spent,
        valid: false,
        error: `${key} puanı ${POINT_BUY_MIN} altına düşürülemez (şu an ${score}).`,
      }
    }
    if (score > POINT_BUY_MAX) {
      return {
        spent,
        remaining: POINT_BUY_BUDGET - spent,
        valid: false,
        error: `Puan Havuzu’nda en yüksek ${POINT_BUY_MAX} alınabilir (${key}: ${score}).`,
      }
    }
    spent += pointBuyCost(score)
  }

  if (spent > POINT_BUY_BUDGET) {
    return {
      spent,
      remaining: POINT_BUY_BUDGET - spent,
      valid: false,
      error: `Bütçe aşıldı: ${spent}/${POINT_BUY_BUDGET} puan.`,
    }
  }

  return { spent, remaining: POINT_BUY_BUDGET - spent, valid: true, error: null }
}

/** Standart Dizgi'de kalan puanlar. Tüm değerler tekil olduğu için basit filtre. */
export function remainingStandardArray(used: readonly number[]): number[] {
  return [...STANDARD_ARRAY].filter((value) => !used.includes(value))
}

/**
 * 4d6 Yukarı At (3 en yüksek) ile yetenek puanları atar.
 * 6 set atılır; sihirbaz kullanıcıya sıralama önerir ama sıralamayı
 * KULLANICI yapar (5e kuralı: sıralama oyuncuya aittir).
 */
export function rollAbilityScores(rng: Rng = secureRng()): number[] {
  const sets: number[] = []
  for (let n = 0; n < 6; n++) {
    sets.push(
      rollFormula(
        { terms: [{ count: 4, sides: 6, keepMode: 'highest', keepCount: 3 }], flat: 0 },
        rng,
      ).total,
    )
  }
  return sets
}

/** Atılan setleri en iyiden en kötüye sıralar (öneri). */
export function sortAbilityScores(sets: readonly number[]): number[] {
  return [...sets].sort((a, b) => b - a)
}

/** Bir yöntem seçiliyken puanların geçerli olup olmadığını kontrol eder. */
export function validateAbilityScores(
  method: AbilityMethod,
  scores: Record<AbilityKey, number>,
): { valid: boolean; error: string | null } {
  const values = ABILITY_KEYS.map((k) => scores[k])

  if (values.some((v) => v == null)) {
    return { valid: false, error: 'Altı yeteneğin de puanı olmalı.' }
  }
  if (values.some((v) => v < POINT_BUY_MIN)) {
    return { valid: false, error: `Hiçbir yetenek ${POINT_BUY_MIN} altına olamaz.` }
  }
  if (values.some((v) => v > SCORE_MAX)) {
    return { valid: false, error: `Hiçbir yetenek ${SCORE_MAX} üstüne olamaz.` }
  }

  if (method === 'pointBuy') {
    const status = evaluatePointBuy(scores)
    return { valid: status.valid, error: status.error }
  }

  if (method === 'standard') {
    // Her puan dizgiden en fazla bir kez alınabilir
    const pool: number[] = [...STANDARD_ARRAY]
    for (const value of values) {
      const index = pool.indexOf(value)
      if (index === -1) {
        return { valid: false, error: `${value} puanı Standart Dizgi'de yok veya tükendi.` }
      }
      pool.splice(index, 1)
    }
    return { valid: true, error: null }
  }

  // rolled: yalnızca aralık kontrolü yukarıda yapıldı
  return { valid: true, error: null }
}

// ---------------------------------------------------------------------------
// BECERİ SEÇİMİ (sınıf + arka plan + ırk)
// ---------------------------------------------------------------------------

export interface SkillChoiceState {
  /** Seçilen beceri anahtarları, sıra korunur. */
  selected: string[]
  /** Sınıfın izin verdiği beceri havuzu. */
  available: { key: string; nameTr: string; alreadyProficient: boolean }[]
  /** Kaç tane daha seçilmeli. */
  remaining: number
  /** Tamam mı? */
  complete: boolean
  /** Alınamayan seçeneklerin nedeni. */
  blocked: Record<string, string>
}

/**
 * Sınıfın beceri havuzunu ve mevcut seçim durumunu hesaplar.
 *
 * 5e kuralları:
 *  - Sınıfın belirttiği listeden seçilir.
 *  - Zaten yetkin olunan beceri TEKRAR seçilemez.
 *  - Sınıf izin vermiyorsa aynı beceri iki kez seçilemez.
 */
export function evaluateSkillChoices(
  classId: string | null,
  chosen: string[],
  alreadyProficient: readonly string[],
  content: ContentIndex,
): SkillChoiceState {
  const cls: ClassEntry | undefined = classId ? content.classes[classId] : undefined
  if (!cls) {
    return { selected: chosen, available: [], remaining: 0, complete: false, blocked: {} }
  }

  // Sınıfın beceri havuzu. 5e metninde yazar ama mekaniksel değildir; bu yüzden
  // `ClassEntry.skillChoicePool` alanında açıkça tutulur (özellik metnine
  // gömülürse sihirbaz havuzu okuyamaz).
  const pool = cls.skillChoicePool

  const already = new Set(alreadyProficient.map((p) => p.replace(/^skill\./, '')))

  const available = pool.map((key) => {
    const skill = SKILLS.find((s) => s.key === key)
    const isProficient = already.has(key)
    return {
      key,
      nameTr: skill?.labelTr ?? key,
      alreadyProficient: isProficient,
    }
  })

  const blocked: Record<string, string> = {}
  for (const option of available) {
    if (option.alreadyProficient) {
      blocked[option.key] = 'Bu beceride zaten yetkinsin.'
    } else if (chosen.filter((c) => c === option.key).length > 1) {
      blocked[option.key] = 'Bu beceri birden fazla kez seçilemez.'
    }
  }

  const uniqueChosen = [...new Set(chosen)]
  const effective = uniqueChosen.filter((c) => !already.has(c))
  const remaining = Math.max(0, cls.skillChoices - effective.length)

  return {
    selected: uniqueChosen,
    available,
    remaining,
    complete: remaining === 0,
    blocked,
  }
}

/** Bir karakterin şu anda yetkin olduğu beceri anahtarları. */
export function collectProficiencies(input: {
  proficiencies: readonly string[]
  choices?: Record<string, string[]>
}): string[] {
  const out = new Set<string>()
  for (const p of input.proficiencies) {
    if (p.startsWith('skill.')) out.add(p)
  }
  for (const ids of Object.values(input.choices ?? {})) {
    for (const id of ids) {
      if (id.startsWith('skill.')) out.add(id)
    }
  }
  return [...out]
}

// ---------------------------------------------------------------------------
// SEVİYE ATLAMA / YETENEK GELİŞTİRME (ASI)
// ---------------------------------------------------------------------------

export interface AsiSlot {
  /** Kaçıncı yetenek geliştirmesi (0 tabanlı). */
  index: number
  /** Hangi seviyede kazanıldı. */
  level: number
  /** Hangi yeteneğe uygulanacak (null = kullanılmadı). */
  target: AbilityKey | null
  /** +1 mi +2 mi. +2 yalnızca tek yeteneğe. */
  amount: 1 | 2
}

export interface AsiState {
  slots: AsiSlot[]
  /** Kullanılabilir slot sayısı. */
  total: number
  /** Kullanılan slot. */
  used: number
  /** Son puanlar (temel + ırk + ASI). */
  finalScores: Record<AbilityKey, number>
  errors: string[]
}

/**
 * Mevcut seviyeye göre kaç adet ASI hakkı kazanıldığını hesaplar.
 * Sınıf seviyesinin `asi` alanından gelir (4/8/12/16/19).
 */
export function asiSlotsForLevel(
  level: number,
  content: ContentIndex,
  classId: string | null,
): { index: number; level: number }[] {
  const cls = classId ? content.classes[classId] : undefined
  if (!cls) return []
  const slots: { index: number; level: number }[] = []
  for (const tier of [...cls.levels].sort((a, b) => a.level - b.level)) {
    if (tier.level > level) break
    for (let n = 0; n < tier.asi; n++) {
      slots.push({ index: slots.length, level: tier.level })
    }
  }
  return slots
}

/**
 * ASI durumunu doğrular ve son puanları hesaplar.
 * @param base       Dizgi/puan-havuzu sonrası TEMEL puanlar
 * @param raceBonus  Irk bonuslari (sabit + oyuncu secimi)
 * @param slots      Kullanıcının doldurduğu ASI slotları
 */
export function evaluateAsi(
  base: Record<AbilityKey, number>,
  raceBonus: Partial<Record<AbilityKey, number>>,
  slots: readonly AsiSlot[],
): AsiState {
  const total = slots.length
  const used = slots.filter((s) => s.target != null).length

  const finalScores = { ...base }
  for (const key of ABILITY_KEYS) finalScores[key] += raceBonus[key] ?? 0
  for (const slot of slots) {
    if (slot.target == null) continue
    finalScores[slot.target] += slot.amount
  }

  const errors: string[] = []
  for (const key of ABILITY_KEYS) {
    if (finalScores[key] > SCORE_MAX) {
      errors.push(`${key} puanı ${SCORE_MAX}’i aşıyor (${finalScores[key]}).`)
    }
  }
  for (const [i, slot] of slots.entries()) {
    if (slot.amount === 2 && slot.target == null) {
      errors.push(`${i + 1}. geliştirmede +2 seçildi ama hedef yetenek yok.`)
    }
  }
  // Aynı slotta hem +1 hem +2 olmamalı (amount tek değer olduğu için doğal)
  const seen = new Set<string>()
  for (const [i, slot] of slots.entries()) {
    if (slot.target == null) continue
    if (slot.amount === 2 && seen.has(slot.target)) {
      errors.push(`${i + 1}. geliştirmede +2 için aynı yetenek birden fazla kullanılmış.`)
    }
    seen.add(slot.target)
  }

  return { slots: [...slots], total, used, finalScores, errors }
}

// ---------------------------------------------------------------------------
// IRK / SINIF / ARKA PLAN ÖZETİ
// ---------------------------------------------------------------------------

export interface RaceSummary {
  race: RaceEntry | null
  /** Oyuncu seçimiyle birlikte nihai yetenek bonusları. */
  abilityBonuses: Partial<Record<AbilityKey, number>>
  /** +1'in gidebileceği yetenekler (Irk seçimi gerektiriyorsa). */
  pendingAsiChoices: { key: AbilityKey; nameTr: string }[]
  /** Seviye kilisine takılmış özellikler. */
  lockedFeatures: { name: string; grantedAtLevel: number }[]
}

export function summarizeRace(
  raceId: string | null,
  level: number,
  content: ContentIndex,
): RaceSummary {
  const race = raceId ? content.races[raceId] : null
  if (!race) {
    return { race: null, abilityBonuses: {}, pendingAsiChoices: [], lockedFeatures: [] }
  }

  const locked = race.features
    .filter((f) => (f.grantedAtLevel ?? 1) > level)
    .map((f) => ({ name: f.name, grantedAtLevel: f.grantedAtLevel ?? 1 }))

  return {
    race,
    abilityBonuses: race.abilityBonuses,
    pendingAsiChoices: [],
    lockedFeatures: locked,
  }
}

/** 5e hizalama listesi. */
export const ALIGNMENTS = [
  'Yaslı İyi',
  'Tarafsız İyi',
  'Kaotik İyi',
  'Yaslı Kötü',
  'Tarafsız Kötü',
  'Kaotik Kötü',
  'Dengeli',
] as const
export type Alignment = (typeof ALIGNMENTS)[number]

// ---------------------------------------------------------------------------
// TASLAK DOĞRULAMA (sihirbazın "devam" butonu)
// ---------------------------------------------------------------------------

export interface CharacterDraft {
  name: string
  alignment: Alignment | ''
  abilityMethod: AbilityMethod
  abilityScores: Record<AbilityKey, number>
  raceId: string | null
  /** Irk seçimi: keşfedilen bölümden gelen +1 (örn. Elf CHA). */
  asiChoices: Partial<Record<AbilityKey, number>>
  choices: Record<string, string[]>
  backgroundId: string | null
  classId: string | null
  level: number
  skillProficiencies: string[]
  asiSlots: AsiSlot[]
}

export interface DraftValidation {
  /** Devam edilebilir mi? */
  canContinue: boolean
  /** Adım adım eksikler. */
  missing: Record<string, string[]>
  /** Tüm hatalar (tek liste). */
  errors: string[]
}

/**
 * Taslağı doğrular. Sihirbazın hangi adımda neyin eksik olduğunu
 * göstermesi için ALAN BAZLI sonuç döner.
 */
export function validateDraft(draft: CharacterDraft, content: ContentIndex): DraftValidation {
  const missing: Record<string, string[]> = {}
  const errors: string[] = []

  // 1) Kimlik
  const kimlik: string[] = []
  if (!draft.name.trim()) kimlik.push('Karakter adı gerekli.')
  if (!draft.alignment) kimlik.push('Hizalama seçilmeli.')
  if (kimlik.length) {
    missing.kimlik = kimlik
    errors.push(...kimlik)
  }

  // 2) Yetenekler
  const yetenek = validateAbilityScores(draft.abilityMethod, draft.abilityScores)
  if (!yetenek.valid) {
    missing.yetenek = [yetenek.error ?? 'Yetenek puanları geçersiz.']
    errors.push(yetenek.error ?? '')
  }

  // 3) Irk
  const irk: string[] = []
  if (!draft.raceId) irk.push('Irk seçilmeli.')
  else {
    const race = content.races[draft.raceId]
    if (!race) irk.push('Seçilen ırk bulunamadı.')
    else {
      for (const group of race.choices) {
        if (group.pending) continue // içerik bekleniyor: engel değil
        if ((group.grantedAtLevel ?? 1) > draft.level) continue // kilitli
        const chosen = draft.choices[group.id] ?? []
        if (chosen.length < group.min) {
          irk.push(
            `“${group.labelTr}” için ${group.min} seçim yapılmalı (${chosen.length} yapıldı).`,
          )
        }
      }
    }
  }
  if (irk.length) {
    missing.irk = irk
    errors.push(...irk)
  }

  // 4) Arka plan
  if (!draft.backgroundId) {
    missing.arkaPlan = ['Arka plan seçilmeli.']
    errors.push('Arka plan seçilmeli.')
  }

  // 5) Sınıf + beceri seçimi
  const sinif: string[] = []
  if (!draft.classId) {
    sinif.push('Sınıf seçilmeli.')
  } else {
    const already = collectProficiencies({
      // ırk + arka plan verdiği beceriler
      proficiencies: buildBaseProficiencies(draft, content),
      choices: draft.choices,
    })
    const skills = evaluateSkillChoices(draft.classId, draft.skillProficiencies, already, content)
    if (!skills.complete) {
      sinif.push(`Beceri seçimi eksik: ${skills.remaining} seçim daha yapılmalı.`)
    }
  }
  if (sinif.length) {
    missing.sinif = sinif
    errors.push(...sinif)
  }

  // 6) Seviye
  if (draft.level < 1 || draft.level > 20) {
    missing.seviye = ['Seviye 1–20 arasında olmalı.']
    errors.push('Seviye 1–20 arasında olmalı.')
  }

  return {
    canContinue: Object.keys(missing).length === 0,
    missing,
    errors,
  }
}

/**
 * Karakterin seviye BAŞINA sahip olduğu beceri yetkinlikleri.
 *
 * ⚠️ Sınıf havuzundan YAPILAN seçimler dahil DEĞİLDİR — bunlar ayrı
 * parametreyle (`chosen`) gelir. Aksi halde "zaten yetkin" kontrolü kendi
 * seçimini geçersiz sayar ve eksik sayıyı yanlış hesaplar.
 */
export function buildBaseProficiencies(draft: CharacterDraft, content: ContentIndex): string[] {
  const out = new Set<string>()

  const bg = draft.backgroundId ? content.backgrounds[draft.backgroundId] : undefined
  for (const p of bg?.skillProficiencies ?? []) out.add(p)

  const race = draft.raceId ? content.races[draft.raceId] : undefined
  for (const feature of race?.features ?? []) {
    for (const p of feature.grantsProficiencies ?? []) {
      if (p.startsWith('skill.')) out.add(p)
    }
  }
  // Irk seçimleriyle gelen beceri yetkinlikleri (örn. İnsan → Investigation)
  for (const ids of Object.values(draft.choices ?? {})) {
    for (const id of ids) if (id.startsWith('skill.')) out.add(id)
  }
  return [...out]
}

/** Taslağı motorun anlayacağı `CharacterInput` biçimine çevirir. */
export function draftToCharacterInput(
  draft: CharacterDraft,
  content: ContentIndex,
): CharacterInput {
  const race = draft.raceId ? content.races[draft.raceId] : undefined
  const background = draft.backgroundId ? content.backgrounds[draft.backgroundId] : undefined

  const proficiencies = new Set<string>(buildBaseProficiencies(draft, content))
  for (const p of draft.skillProficiencies) proficiencies.add(p)

  // Arka plan alet/dil yetkinlikleri
  for (const p of background?.toolProficiencies ?? []) proficiencies.add(p)
  for (const p of background?.languages ?? []) proficiencies.add(p)

  // Sınıf zırh/silah yetkinlikleri
  const cls = draft.classId ? content.classes[draft.classId] : undefined
  for (const p of cls?.armorProficiencies ?? []) proficiencies.add(`armor.${p}`)
  for (const p of cls?.weaponProficiencies ?? []) proficiencies.add(`weapon.${p}`)

  // Irk cantrip'leri
  for (const c of race?.cantrips ?? []) proficiencies.add(`cantrip.${c}`)

  // ASI + ırk seçimi bonuslarını birleştir
  const asiBonuses: Partial<Record<AbilityKey, number>> = { ...draft.asiChoices }
  const slots = asiSlotsForLevel(draft.level, content, draft.classId)
  const asiState = evaluateAsi(draft.abilityScores, draft.asiChoices, draft.asiSlots)
  void slots
  for (const key of ABILITY_KEYS) {
    const fromAsi = asiState.finalScores[key] - (draft.abilityScores[key] ?? 10)
    if (fromAsi > 0) asiBonuses[key] = fromAsi
  }

  return {
    ruleset: 'homebrew',
    level: draft.level,
    classes: draft.classId ? [{ classId: draft.classId, level: draft.level, isPrimary: true }] : [],
    raceId: draft.raceId,
    backgroundId: draft.backgroundId,
    abilityScores: draft.abilityScores,
    asiBonuses,
    proficiencies: [...proficiencies],
    inventory: [],
    conditions: [],
    choices: draft.choices,
  }
}

/**
 * 5e seviye/XP eşiği tablosu. İndeks = seviye.
 * 20. seviye 600.000 XP'de — 19 ile 20 arası en büyük sıçramadır.
 */
const XP_TABLE = [
  0, 300, 900, 2700, 6500, 14000, 23000, 34000, 48000, 64000, 85000, 100000, 120000, 140000, 165000,
  195000, 225000, 265000, 305000, 355000, 600000,
] as const

/** Bir seviyenin XP eşiği. */
export function xpForLevel(level: number): number {
  return XP_TABLE[Math.max(0, Math.min(20, Math.round(level)))] ?? 0
}

/** XP'den seviye (ters eşleme). XP 20 seviye eşiğinin üstündeyse 20 döner. */
export function levelForXp(xp: number): number {
  let level = 0
  for (let i = 0; i < XP_TABLE.length; i++) {
    if (xp >= XP_TABLE[i]!) level = i
  }
  return level
}

/** Yardımcı: verilen puanlarla bir yeteneğin DC'si. */
export function abilitySaveDc(score: number, proficiencyBonus: number): number {
  return 8 + proficiencyBonus + abilityModifier(score)
}
